import React from "react";
import  Table from '../base-table';
import type { TableRef } from '../base-table';
import type { ColumnDef, SortingState, ColumnFiltersState, ColumnOrderState, RowSelectionState, PaginationState, ExpandedState, TableMeta } from "@tanstack/react-table";
import { getCoreRowModel } from "@tanstack/react-table";
import { Consignment } from "../../graphql/graphql";
import { TableMessage } from "../../shared/TableMessage";
import { TableSpinner } from "../../shared/TableSpinner";
import { useNestedTableMaxHeight } from "../../shared/useNestedTableMaxHeight";

interface ConsignmentsTableProps {
  tableRef: React.Ref<TableRef>;
  columns?: () => ColumnDef<Consignment, any>[];
  data: Consignment[];
  getRowId?: (originalRow: Consignment) => string;
  enableRowSelection?: boolean;
  enableColumnFiltersSidePanel?: boolean;
  enablePagination?: boolean;
  onPaginationChange?: (pagination: PaginationState) => void;
  pageIndex?: number;
  pageSize?: number;
  totalCount?: number;
  parentTableRef?: React.RefObject<TableRef>; // NEW: Reference to parent table
  state: {
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    setSorting: (newSorting: SortingState) => void;
    setColumnFilters: (newFilters: ColumnFiltersState) => void;
    columnOrder: ColumnOrderState;
    setColumnOrder: (newOrder: ColumnOrderState) => void;
    setRowSelection: (newRowSelection: RowSelectionState) => void;
    rowSelection?: RowSelectionState;
    resetRowSelection?: (defaultState?: boolean) => void;
    columnVisibility?: Record<string, boolean>;
    setColumnVisibility?: (newVisibility: Record<string, boolean>) => void;
    pagination?: { totalCount?: number }
    globalSearch?: string;
    expanded?: ExpandedState;
    columnFiltersSidePanelIsOpen?: boolean; 
  };
  isLoading: boolean;
  error: any;
  enableGlobalFilter?: boolean;
  onGlobalFilterChange?: (filter: string) => void;
  onExpandedChange?: (expanded: ExpandedState) => void;
  onColumnFiltersSidePanelOpenChange?: (isOpen: boolean) => void;
  meta: TableMeta<Consignment>;
}

export const ConsignmentsTable = React.memo(
    function ConsignmentsTable({ 
        tableRef,
        columns,
        data,
        state,
        isLoading,
        error,
        getRowId,
        enablePagination,
        pageIndex = 0,
        pageSize = 10,
        totalCount,
        onPaginationChange,
        enableGlobalFilter,
        onGlobalFilterChange,
        onExpandedChange,
        meta,
        onColumnFiltersSidePanelOpenChange,
        parentTableRef, // NEW
    }: ConsignmentsTableProps & {
        onColumnFiltersSidePanelOpenChange?: (isOpen: boolean) => void;
    }) {

    // Calculate max height for nested table
    const { nestedContainerRef, maxHeight } = useNestedTableMaxHeight({
      parentTableRef,
      additionalOffset: 100, // Extra padding
      enabled: !!parentTableRef, // Only enable if we have a parent ref
    });

    if (error) return <div style={{ color: "red" }}>Error: {error.message}</div>;

    if (data) {
        return (
            <div ref={nestedContainerRef} style={{ height: "100%" }}>
        <Table
            className="consignments-table"
            isNested={true}
            fullWidth
            ref={tableRef}
            columns={columns ? (columns() as any) : []}
            getCoreRowModel={getCoreRowModel()}
            data={data}
            enableColumnFilters={false}
            enableColumnFiltersSidePanel={false}
            enableSorting={true}
            disableStickyHeader={false} // Enable sticky within nested table
            allowMultiSort={true}
            enableRowSelection={false}
            enablePagination={data?.length > 1 && enablePagination}
            manualPagination={true}
            manualFiltering={true}
            manualSorting={true}
            enableMultiRowSelection={false}
            enableGlobalFilter={enableGlobalFilter}
            initialGlobalFilter={state.globalSearch}
            onPaginationChange={onPaginationChange}
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={state.pagination?.totalCount}
            state={{
                columnFilters: state.columnFilters,
                sorting: state.sorting,
                columnOrder: state.columnOrder,
                setColumnOrder: state.setColumnOrder,
                rowSelection: state.rowSelection,
                columnVisibility: state.columnVisibility,
                expanded: state.expanded,
                columnFiltersSidePanelIsOpen: state.columnFiltersSidePanelIsOpen, 
            }}
            onSortChange={state.setSorting}
            onColumnFiltersChange={state.setColumnFilters}
            onRowSelectionChange={state.setRowSelection}
            onGlobalFilterChange={onGlobalFilterChange}
            getRowId={getRowId}
            resetRowSelection={state.resetRowSelection}
            onExpandedChange={onExpandedChange}
            onColumnFiltersSidePanelOpenChange={onColumnFiltersSidePanelOpenChange}
            meta={meta}
            noDataText={isLoading ? "Loading consignments..." : "No consignments found."}
            noDataContent={isLoading ? <TableSpinner /> : <TableMessage msg="No Consignments Found" />}
            style={{
              maxHeight: maxHeight ? `${maxHeight}px` : undefined,
              overflow: 'auto',
            }}
        />
        </div>
        );
    }
    return <></>;
});

